/**
 * Package for storing APIResult data as state. At its base, this package
 * defines the basic state mechanisms. Each API has an APIData instance
 * representing not only the lifecycle of the API request itself but also the
 * results.
 *
 * This APIData can either be an EmptyAPIData, LoadingAPIData, or a
 * FilledAPIData. An EmptyAPIData represents an API that has never been called.
 * A LoadingAPIData represents an API that's currently in progress (loading).
 * Once an API has succeeded or failed, its data will be replaced with a
 * FilledAPIData structure. To check for each type, simply use "isEmpty",
 * "isLoading", and "isFilled".
 *
 * This package also contains a mechanism to maintain an API's state via redux
 * and redux-sagas. This is accomplished via the createAPIActionGenerator
 * function and its store and saga functions. First, you create your generator:
 *
 *  type MyRequest = {
 *    request: string;
 *  };
 *  type MyType = {
 *    response: string;
 *  };
 *  type MyError = {
 *    message: string;
 *  };
 *  const generator = createAPIActionGenerator<MyRequest, MyType, MyError>({
 *    prefix: 'MY_ACTION',
 *  });
 *  const store = generator.store();
 *
 * This store can now be used with combineReducers in order to inject the state
 * lifecycle of your action into a store - the results of which will be an
 * APIData of your MyType and MyError types.
 *
 * In order to generate a saga for your action, you can also use the
 * saga function. This saga will request the configured API via a "call" effect
 * and either issue the success or failure action depending on the result:
 *
 *  const mySaga = generator.saga(
 *    (api, request) => call(api.get, '/my_url/'),
 *  );
 *
 * If you want to have more control over the saga functionality, go ahead and
 * create your own:
 *
 *  function* mySaga(action: APIAction) {
 *    const { request } = action;
 *    const response = yield call(generator.api.get, "/my/url");
 *    yield call(generator.handleResponse, { request, response });
 *    ...
 *  }
 *
 * But you'll have to call "takeEvery" with "generator.types.request" as the
 * first argument and your saga as the second.
 *
 * This package also supports "indirect" API data. For instance, if you have a
 * collection of items and not just a single item. You could create a request
 * like:
 *
 *  type MyRequest = {
 *    uuid?: string;
 *  };
 *
 * And create the generator like:
 *
 *  const generator = createIndirectAPIActionGenerator<MyRequest, MyType, MyError>({
 *    prefix: 'MY_ACTION',
 *    getKey: (request: MyRequest) => request.uuid,
 *  });
 *
 * When the call finishes, it'll build out the store like:
 *
 *  {
 *    my_uuid1: <entry>,
 *    ...
 *  }
 *
 * Rather than a flat store. You'd use the saga like:
 *
 *  const mySaga = generator.saga(
 *    (api, request) => call(api.get, `/my_url/${request.uuid}`)
 *  );
 *
 * In order to query the single entry.
 */
import { API, APIOptions, APIResult, createAPI } from '../api';
import { call, put, takeEvery } from 'redux-saga/effects';
import { CallEffect, ForkEffect } from '@redux-saga/core/effects';

/******************************************************************************
 * Basic Functionality
 *****************************************************************************/

/** Type representing an API request that hasn't even been called. */
export type EmptyAPIData = {
  isEmpty: true;
  isLoading: false;
  isFilled: false;
};

/** State representing that the API hasn't been called */
export const emptyResults: EmptyAPIData = {
  isEmpty: true,
  isLoading: false,
  isFilled: false,
};

/** Type representing an API request that is currently in progress. */
export type LoadingAPIData = {
  isLoading: true;
  isFilled: false;
  isEmpty: false;
};

/** State representing an API that's loading */
export const loadingResults: LoadingAPIData = {
  isLoading: true,
  isFilled: false,
  isEmpty: false,
};

/**
 * Type representing a filled API request. It wraps an APIResult with extra
 * state so that its status can be tracked.
 */
export type FilledAPIData<T = unknown, E = unknown> = APIResult<T, E> & {
  /** Whether the request is in progress */
  isLoading: false;
  isFilled: true;

  /** Whether the request has even been called */
  isEmpty: false;
};

/**
 * State representing an API request. It can either be blank or filled with
 * results from the Api project.
 */
export type APIData<T = unknown, E = unknown> =
  | EmptyAPIData
  | LoadingAPIData
  | FilledAPIData<T, E>;

/**
 * Store results from an API
 *
 * @param result - result to store
 */
export function storeResults<T = unknown, E = unknown>(
  result: APIResult<T, E>
): FilledAPIData<T, E> {
  return {
    ...result,
    isLoading: false,
    isFilled: true,
    isEmpty: false,
  };
}

/******************************************************************************
 * Redux integration
 *****************************************************************************/

/** Arguments to provide to APIRequest instances */
export type APIRequest<T = unknown, E = unknown> = {
  /** Function to call when the API request saga completes */
  callback?: (entry: APIResult<T, E>) => void;
};

/** Interface used by the apiRequestStore for representing an action */
export interface APIAction<R = unknown, T = unknown, E = unknown> {
  type: string;
  request?: R;
  response?: APIResult<T, E>;
}

/** Function representing the return type of apiRequestStore */
export type APIRequestStoreFunction<R, T, E> = (
  state: APIData<T>,
  action: APIAction<R, T>
) => APIData<T>;

/** Set of action types handled by APIActionGenerator */
export type APIActionTypes = {
  clear: string;
  request: string;
  success: string;
  failure: string;
};

/** Options to be used with the createAPIActionGenerator function below */
export type APIActionGeneratorOptions = {
  prefix: string;
  api?: APIOptions;
};

/** type representing a generator for API result */
export type APISagaGenerator<R = unknown> = (
  request: R
) => (string | undefined) | Generator<any, string | undefined, any>;

/** Options to be used with the customSaga function below */
export type APISagaOptions<
  R extends APIRequest<T, E>,
  T = unknown,
  E = unknown
> = {
  generate: APISagaGenerator<R>;
  onSuccess?: (
    request: R,
    response: APIResult<T, E>
  ) => Generator<any, any, any> | void;
};

export type APIRequestResponse<R extends APIRequest<T, E>, T, E> = {
  request: R;
  response: APIResult<T, E>;
};

export type BaseAPIActionGenerator<R extends APIRequest<T, E>, T, E> = {
  api: API<T, E>;
  types: APIActionTypes;

  /** Create a clear action from the given request */
  clear: (request?: R) => APIAction<R, T, E>;

  /** Create a request action from the given request */
  request: (request: R) => APIAction<R, T, E>;

  /** Create a success action from the given request and response */
  success: (request: R, response: APIResult<T, E>) => APIAction<R, T, E>;

  /** Create a failure action from the given request and response */
  failure: (request: R, response: APIResult<T, E>) => APIAction<R, T, E>;

  /**
   * Function for handling the API response to a request
   */
  handleResponse: (
    apiRequestResponse: APIRequestResponse<R, T, E>
  ) => Generator<any, any, any>;
};

export type APIActionGenerator<
  R extends APIRequest<T, E>,
  T,
  E
> = BaseAPIActionGenerator<R, T, E> & {
  /** Create a store that handles actions for the given generator */
  store: () => APIRequestStoreFunction<R, T, E>;

  /**
   * Function to generate a takeEvery that handles the "request" of the given
   * APITypes.
   *
   * @param submitRequest - effect representing the request
   */
  saga: (
    submitRequest: (api: API<T, E>, request: R) => CallEffect,
    onSuccess?: (request: R, response: APIResult<T, E>) => void
  ) => ForkEffect;

  /**
   * Function to generate a takeEvery that handles the "request" of the given
   * APITypes with customizable options.
   *
   * @param sagaOpts - options to customize the saga operation
   */
  customSaga: (sagaOpts: APISagaOptions<R, T, E>) => ForkEffect;
};

/**
 * Function encapsulating common redux action types to be used for generating
 * state and saga logic
 */
export function createAPIActionGenerator<
  R extends APIRequest<T, E>,
  T = unknown,
  E = unknown
>(opts: APIActionGeneratorOptions): APIActionGenerator<R, T, E> {
  const api = createAPI<T, E>(opts.api);
  const types = {
    clear: `${opts.prefix}/CLEAR`,
    request: `${opts.prefix}/REQUEST`,
    success: `${opts.prefix}/SUCCESS`,
    failure: `${opts.prefix}/FAILURE`,
  };

  const clear = (request?: R) => {
    return {
      type: types.clear,
      request,
    };
  };

  const request = (request: R): APIAction<R, T, E> => {
    return {
      type: types.request,
      request,
    };
  };

  const success = (request: R, response: APIResult<T, E>) => {
    return {
      type: types.success,
      request,
      response,
    };
  };

  const failure = (request: R, response: APIResult<T, E>) => {
    return {
      type: types.failure,
      request,
      response,
    };
  };

  const store =
    () =>
    (state: APIData<T> = emptyResults, action: APIAction<R, T>) => {
      switch (action.type) {
        case types.clear: {
          return emptyResults;
        }

        case types.request: {
          return loadingResults;
        }

        case types.failure:
        case types.success: {
          const { response } = action;

          // We shouldn't hit this in practice as the "response" value should
          // always be filled out. However, in the case where we do get an empty
          // response, go ahead and just return the state as we can't pass
          // undefined into storeResults.
          if (!response) {
            return state;
          }
          return storeResults(response);
        }

        default:
          return state;
      }
    };

  function* handleResponse({ request, response }: APIRequestResponse<R, T, E>) {
    const { callback } = request;
    yield put(
      response.success ? success(request, response) : failure(request, response)
    );

    if (callback) {
      yield call(callback, response);
    }
  }

  const saga = (
    submitRequest: (api: API<T, E>, request: R) => CallEffect,
    onSuccess?: (request: R, response: APIResult<T, E>) => void
  ) => {
    // TODO: Maybe split this functionality into separate functions to make it
    // easier to inject functionality before and after? Or provide an options
    // type for more hooks here?
    return takeEvery(
      // Configure the given request to be handled by this saga
      types.request,

      // The body of the handler saga
      function* (action: APIAction<R, T>) {
        const { request } = action;
        if (request === undefined) {
          return;
        }

        const response: APIResult<T, E> = yield submitRequest(api, request);
        yield call(handleResponse, { request, response });

        const { success } = response;
        if (success && onSuccess) {
          yield call(onSuccess, request, response);
        }
      }
    );
  };

  const customSaga = (sagaOpts: APISagaOptions<R, T, E>) => {
    return takeEvery(types.request, function* (action: APIAction<R, T>) {
      const { request: req } = action;
      if (req === undefined) {
        return;
      }

      const { generate, onSuccess } = sagaOpts;
      const url: string = yield call(generate, req);
      const response: APIResult<T, E> = yield call(api.get, url);
      yield call(handleResponse, { request: req, response });

      const { success } = response;
      if (success && onSuccess) {
        yield call(onSuccess, req, response);
      }
    });
  };

  return {
    api,
    types,
    clear,
    request,
    success,
    failure,
    store,
    saga,
    customSaga,
    handleResponse,
  };
}

/******************************************************************************
 * Indirect data
 *****************************************************************************/

/**
 * Type representing the store data of the createIndirectAPIRequestStore
 * function
 */
export type IndirectAPIData<T = unknown, E = unknown> = Record<
  string,
  APIData<T, E>
>;

/** Empty results of the indirectAPIRequestStore function */
export const emptyIndirectAPIData = {};

/**
 * Function representing the return value of the
 * IndirectAPIActionGenerator.store function
 */
export type IndirectAPIRequestStoreFunction<
  R = unknown,
  T = unknown,
  E = unknown
> = (
  state: IndirectAPIData<T, E>,
  action: APIAction<R, T, E>
) => IndirectAPIData<T, E>;

export type IndirectAPIActionGenerator<R extends APIRequest<T, E>, T, E> = Omit<
  APIActionGenerator<R, T, E>,
  'store'
> & {
  /** Create a store that handles actions for the given generator */
  store: () => IndirectAPIRequestStoreFunction<R, T, E>;
};

/** Options to be used with the createIndirectAPIActionGenerator function below */
export type IndirectAPIActionGeneratorOptions<R> = APIActionGeneratorOptions & {
  /**
   * Hook for determining the indirect "key" from the request so that the
   * resulting entries can be organized properly.
   */
  getKey: (request: R) => string;
};

/**
 * Function encapsulting common redux action types to be used for generating
 * state and saga logic
 */
export function createIndirectAPIActionGenerator<
  R extends APIRequest<T, E>,
  T = unknown,
  E = unknown
>(
  opts: IndirectAPIActionGeneratorOptions<R>
): IndirectAPIActionGenerator<R, T, E> {
  const { getKey } = opts;
  const generator = createAPIActionGenerator<R, T, E>(opts);
  const { types } = generator;
  const store =
    () =>
    (
      state: IndirectAPIData<T, E> = emptyIndirectAPIData,
      action: APIAction<R, T, E>
    ) => {
      const basicStore = generator.store();
      switch (action.type) {
        case types.clear: {
          const { request } = action;
          if (request === undefined) {
            return emptyIndirectAPIData;
          }

          // Only clear out the given key if a request was given.
          const key = getKey(request);
          const apiState = state[key] || emptyResults;
          return {
            ...state,
            [key]: basicStore(apiState, action),
          };
        }

        case types.request:
        case types.failure:
        case types.success: {
          const { request } = action;
          if (request === undefined) {
            return state;
          }

          const key = getKey(request);
          const apiState = state[key] || emptyResults;
          return {
            ...state,
            [key]: basicStore(apiState, action),
          };
        }

        default:
          return state;
      }
    };
  return {
    ...generator,
    store,
  };
}
